import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { s3PublicUrl } from '../../../utils/s3PublicUrl';
import { resolvePageBackground } from '../utils/pageBackground';
import '../future.css';

export { resolvePageBackground } from '../utils/pageBackground';

export function popupHasContent(image, text) {
  return Boolean(String(image ?? '').trim()) || Boolean(String(text ?? '').trim());
}

// When `object-fit: contain` letterboxes the video (mobile), the video
// element's own box still spans the full fixed container — callers that
// need to lay out content right below the actual visible video frame (not
// the empty letterbox padding) need the real content rect, which depends on
// the video's intrinsic aspect ratio and can't be known from CSS alone.
// Returns null when the video isn't letterboxed (e.g. `cover` on desktop),
// so callers can fall back to their own default layout.
function computeLetterboxedVideoRect(videoEl) {
  if (!videoEl) return null;
  if (window.getComputedStyle(videoEl).objectFit !== 'contain') return null;
  const rect = videoEl.getBoundingClientRect();
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  if (!vw || !vh || !rect.width || !rect.height) return null;
  const mediaAspect = vw / vh;
  const containerAspect = rect.width / rect.height;
  if (mediaAspect <= containerAspect) return null; // fills height edge-to-edge, no vertical letterbox
  const contentHeight = rect.width / mediaAspect;
  const contentTop = rect.top + (rect.height - contentHeight) / 2;
  return { top: contentTop, bottom: contentTop + contentHeight };
}

export function PageBg({ desk, mob, deskVideo = '', loop = true, onVideoEnded, onVideoRect, className = '' }) {
  const deskUrl = s3PublicUrl(desk);
  const mobUrl = s3PublicUrl(mob) || deskUrl;
  const videoUrl = s3PublicUrl(deskVideo);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!onVideoRect) return undefined;
    if (!videoUrl) {
      onVideoRect(null);
      return undefined;
    }
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    const report = () => onVideoRect(computeLetterboxedVideoRect(videoEl));
    report();
    videoEl.addEventListener('loadedmetadata', report);
    window.addEventListener('resize', report);
    window.addEventListener('orientationchange', report);
    return () => {
      videoEl.removeEventListener('loadedmetadata', report);
      window.removeEventListener('resize', report);
      window.removeEventListener('orientationchange', report);
    };
  }, [videoUrl, onVideoRect]);

  // One uploaded video is used on both desktop and mobile — CSS switches the
  // fit between them (cover full-bleed on desktop, contain/centered on
  // mobile so nothing gets cropped on a narrow screen).
  if (videoUrl) {
    return (
      <video
        ref={videoRef}
        className={`fi-page-bg fi-page-bg--video ${className}`}
        src={videoUrl}
        autoPlay
        muted
        loop={loop}
        playsInline
        onEnded={onVideoEnded}
        aria-hidden
      />
    );
  }

  const style = {
    '--fi-bg-desk': deskUrl ? `url("${deskUrl}")` : 'none',
    '--fi-bg-mob': mobUrl ? `url("${mobUrl}")` : 'none',
  };
  return <div className={`fi-page-bg ${className}`} style={style} aria-hidden />;
}

export function StageScreen({ pagesBg, pageKey, stage, deskVideo = '', className = '', children }) {
  const { desk, mob } = resolvePageBackground(pagesBg, pageKey, stage);
  return (
    <>
      <PageBg desk={desk} mob={mob} deskVideo={deskVideo} />
      {children}
    </>
  );
}

export function StagePopup({
  image,
  text,
  boldText,
  onNext,
  nextLabel = 'Next',
  nextButtonImage,
  variant = 'auto',
  alignTop = false,
}) {
  const [visible, setVisible] = useState(false);
  const [nextImgFailed, setNextImgFailed] = useState(false);
  const [popupImgFailed, setPopupImgFailed] = useState(false);
  const themeData = useSelector((state) => state.theme?.data);
  const imgUrl = s3PublicUrl(image);
  const nextImg = s3PublicUrl(
    nextButtonImage || themeData?.next_button_image || 'NEXT.png'
  );
  const showNextImg = Boolean(nextImg) && !nextImgFailed;
  const showPopupImg = Boolean(imgUrl) && !popupImgFailed;
  const useImagePopup = variant === 'image' || (variant === 'auto' && Boolean(imgUrl));
  const hasTextContent = Boolean(String(boldText ?? '').trim()) || Boolean(String(text ?? '').trim());
  const hasNothingToShow = !showPopupImg && !hasTextContent;

  useEffect(() => {
    setPopupImgFailed(false);
  }, [imgUrl]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Covers the case where image/text look non-empty to the caller but the
  // image 404s and there's no text fallback — skip instead of showing a
  // blank framed popup with just a Next button.
  useEffect(() => {
    if (hasNothingToShow) onNext?.();
  }, [hasNothingToShow, onNext]);

  if (hasNothingToShow) return null;

  if (useImagePopup) {
    return (
      <div
        className={`fi-popup-overlay fi-popup-overlay--intro ${alignTop ? 'fi-popup-overlay--top' : 'fi-popup-overlay--centered'} ${visible ? 'fi-popup-overlay--visible' : ''}`}
      >
        <div className={`fi-popup-intro ${visible ? 'fi-popup-intro--visible' : ''}`}>
          {showPopupImg ? (
            <img
              src={imgUrl}
              alt=""
              className="fi-popup-intro__image"
              onError={() => setPopupImgFailed(true)}
            />
          ) : (
            <div className="fi-popup-frame fi-popup-frame--visible">
              {boldText ? <p className="fi-popup-frame__bold">{boldText}</p> : null}
              {text ? <p className="fi-popup-frame__text">{text}</p> : null}
            </div>
          )}
          {showPopupImg && (boldText || text) ? (
            <p className="fi-popup-intro__caption">
              {boldText ? <strong className="fi-popup-frame__bold">{boldText}</strong> : null}
              {boldText && text ? <br /> : null}
              {text}
            </p>
          ) : null}
          <div className="fi-popup-intro__actions">
            {showNextImg ? (
              <button
                type="button"
                className="fi-popup-next-img"
                onClick={onNext}
                aria-label={nextLabel}
              >
                <img src={nextImg} alt="" onError={() => setNextImgFailed(true)} />
              </button>
            ) : (
              <button type="button" className="fi-popup-next-btn" onClick={onNext}>
                {nextLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fi-popup-overlay ${visible ? 'fi-popup-overlay--visible' : ''}`}>
      <div className={`fi-popup-frame ${visible ? 'fi-popup-frame--visible' : ''}`}>
        {boldText ? <p className="fi-popup-frame__bold">{boldText}</p> : null}
        {text ? <p className="fi-popup-frame__text">{text}</p> : null}
        <div className="fi-popup-intro__actions">
          {showNextImg ? (
            <button
              type="button"
              className="fi-popup-next-img"
              onClick={onNext}
              aria-label={nextLabel}
            >
              <img src={nextImg} alt="" onError={() => setNextImgFailed(true)} />
            </button>
          ) : (
            <button type="button" className="fi-popup-next-btn" onClick={onNext}>
              {nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function useStartEndPopups(stageData, onReady) {
  const [phase, setPhase] = useState('start');

  useEffect(() => {
    if (!stageData?.start_popup_text && !stageData?.start_popup_image) {
      setPhase('game');
      onReady?.();
    }
  }, [stageData, onReady]);

  const showStart = phase === 'start';
  const showEnd = phase === 'end';

  const dismissStart = () => {
    setPhase('game');
    onReady?.();
  };

  const triggerEnd = () => setPhase('end');

  return { showStart, showEnd, dismissStart, triggerEnd, gameActive: phase === 'game' };
}
